import { useRef, useEffect } from "react";

// Uses the ref + effect idiom (write in effect, read during render) instead of
// the "store previous value during render" setState pattern. That setState
// variant infinite-loops whenever `value` is a non-primitive recreated each
// render (e.g. the navigation store's `segments` array in TopBar): the
// `prev !== value` guard is always true, so it setStates every render →
// "Maximum update depth exceeded". The ref+effect form is loop-proof for any
// value type and matches the canonical usePrevious idiom.
const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  // react-hooks/refs forbids reading a ref during render, but that is exactly
  // what usePrevious must do, and every setState-based alternative
  // infinite-loops when the input is an unstable reference (e.g. a navigation
  // store array) — which is the crash this replaced. The ref is written only in
  // the effect above, so this read is safe.
  // eslint-disable-next-line react-hooks/refs
  return ref.current;
};

export default usePrevious;
